// Admin API client — all calls use bearer-token auth.
// The token is entered once in the admin login screen and stored in sessionStorage.

const API = (import.meta.env.VITE_API_URL ?? "/api").replace(/\/$/, "");

function headers(): HeadersInit {
  const token = sessionStorage.getItem("admin_token") ?? "";
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method, headers: headers() };
  if (body !== undefined) init.body = JSON.stringify(body);
  const res = await fetch(`${API}${path}`, init);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || res.statusText);
  }
  return res.json() as Promise<T>;
}

// ── Styles ──
export interface AdminStyle {
  id: string;
  label: string;
  category: string;
  /** Optional collection id (audience grouping — Girls, Boys, …). Empty/undefined = unassigned. */
  collection?: string;
  tagline: string;
  gradient: [string, string];
  imageUrl?: string;
  promptTemplate: string;
  enabled?: boolean;
  sortOrder?: number;
}

export const adminStyles = {
  list: () => api<{ styles: AdminStyle[] }>("GET", "/admin/styles"),
  create: (s: Partial<AdminStyle>) => api<{ style: AdminStyle }>("POST", "/admin/styles", s),
  update: (id: string, s: Partial<AdminStyle>) => api<{ style: AdminStyle }>("PUT", `/admin/styles/${id}`, s),
  delete: (id: string) => api<{ ok: boolean }>("DELETE", `/admin/styles/${id}`),
  reorder: (order: string[]) => api<{ styles: AdminStyle[] }>("PUT", "/admin/styles-reorder", { order }),
};

// ── Categories ──
// Categories are managed in their own tab so admins can name them freely
// (not the historical hardcoded list of 6). Templates reference them by id.
export interface AdminCategory {
  id: string;
  label: string;
  sortOrder?: number;
}

export const adminCategories = {
  list: () => api<{ categories: AdminCategory[] }>("GET", "/admin/categories"),
  create: (c: Partial<AdminCategory>) => api<{ category: AdminCategory }>("POST", "/admin/categories", c),
  update: (id: string, c: Partial<AdminCategory>) => api<{ category: AdminCategory }>("PUT", `/admin/categories/${id}`, c),
  delete: (id: string) => api<{ ok: boolean }>("DELETE", `/admin/categories/${id}`),
  reorder: (order: string[]) => api<{ categories: AdminCategory[] }>("PUT", "/admin/categories-reorder", { order }),
};

// ── Collections ──
// Audience grouping (Girls, Boys, Weddings…). Sibling concept to categories,
// different axis. Each style can be assigned to at most one collection.
export interface AdminCollection {
  id: string;
  label: string;
  sortOrder?: number;
}

export const adminCollections = {
  list: () => api<{ collections: AdminCollection[] }>("GET", "/admin/collections"),
  create: (c: Partial<AdminCollection>) => api<{ collection: AdminCollection }>("POST", "/admin/collections", c),
  update: (id: string, c: Partial<AdminCollection>) => api<{ collection: AdminCollection }>("PUT", `/admin/collections/${id}`, c),
  delete: (id: string) => api<{ ok: boolean }>("DELETE", `/admin/collections/${id}`),
  reorder: (order: string[]) => api<{ collections: AdminCollection[] }>("PUT", "/admin/collections-reorder", { order }),
};

export async function analyzeStyle(file: File): Promise<{ analysis: Omit<AdminStyle, "id"> }> {
  const fd = new FormData();
  fd.append("file", file);
  
  const token = sessionStorage.getItem("admin_token") ?? "";
  const res = await fetch(`${API}/admin/analyze-style`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || res.statusText);
  }
  return res.json();
}

// ── Mockup Scenes ──
export interface SceneConfig {
  id: string;
  label: string;
  type: "product_flat" | "room_scene";
  bgImage?: string;
  artPosition?: {
    x: number; y: number; w: number; h: number;
    rotation?: number; skewX?: number; skewY?: number;
    /** 4-corner perspective override — normalized coords (0..1) relative to bg image */
    corners?: { tl: [number, number]; tr: [number, number]; bl: [number, number]; br: [number, number] };
  };
  shadow?: boolean;
  frameStyle?: string;
  enabled?: boolean;
  sortOrder?: number;
}

export type ScenesMap = Record<string, SceneConfig[]>;

export const adminScenes = {
  list: () => api<{ scenes: ScenesMap }>("GET", "/admin/mockup-scenes"),
  create: (productType: string, s: Partial<SceneConfig>) =>
    api<{ scene: SceneConfig }>("POST", `/admin/mockup-scenes/${productType}`, s),
  update: (productType: string, sceneId: string, s: Partial<SceneConfig>) =>
    api<{ scene: SceneConfig }>("PUT", `/admin/mockup-scenes/${productType}/${sceneId}`, s),
  delete: (productType: string, sceneId: string) =>
    api<{ ok: boolean }>("DELETE", `/admin/mockup-scenes/${productType}/${sceneId}`),
  reorder: (productType: string, order: string[]) =>
    api<{ scenes: SceneConfig[] }>("PUT", `/admin/mockup-scenes/${productType}/reorder`, { order }),
};

// ── Products ──
export interface AdminProduct {
  type: string;
  printifyId: number;
  label: string;
  subtitle: string;
  material: string;
  features: string[];
  frameColors?: string[] | null;
  sizes: Array<{
    label: string;
    width: number;
    height: number;
    printifyCost: number;
    retailPrice: number;
  }>;
  enabled?: boolean;
}

export const adminProducts = {
  list: () => api<{ products: AdminProduct[] }>("GET", "/admin/products"),
  update: (type: string, p: Partial<AdminProduct>) =>
    api<{ product: AdminProduct }>("PUT", `/admin/products/${type}`, p),
};

// ── Orders (paid) ──
export interface AdminOrderItem {
  product_title: string;
  dad_name: string | null;
  style_id: string | null;
  quantity: number;
  generation_id: string;
  /** Generation status: "final" once the print file is ready. */
  print_status: string;
  /** Print-ready 300-DPI file URL — null until the print pipeline finishes. */
  print_url: string | null;
  /** Watermarked preview — used as the admin thumbnail. */
  preview_url: string | null;
}

export interface AdminOrder {
  order_number: number;
  email: string | null;
  created_at: string;
  total_price: string | null;
  currency: string | null;
  items: AdminOrderItem[];
}

export const adminOrders = {
  list: () => api<{ orders: AdminOrder[] }>("GET", "/admin/orders"),
};

// ── Mockup BG Auto-Detect ──
// Asks Gemini Vision to identify the art-placement region in an uploaded bg.
// The admin clicks "✨ Auto-detect corners" after uploading a bg, then the
// returned coords pre-fill the perspective editor.
export interface AnalyzedMockup {
  corners: {
    tl: [number, number];
    tr: [number, number];
    bl: [number, number];
    br: [number, number];
  };
  label_suggestion: string;
  scene_type_suggestion: "room_scene" | "product_flat";
  frame_style_suggestion: "none" | "thin_white" | "thin_black" | "colored";
  notes: string;
}

export function analyzeMockupBg(bgImageUrl: string): Promise<{ analysis: AnalyzedMockup }> {
  return api<{ analysis: AnalyzedMockup }>("POST", "/admin/analyze-mockup-bg", { bgImageUrl });
}

// ── Live Composite Preview ──
// Returns a Blob (image/png) that callers can wrap with URL.createObjectURL.
// Used by SceneEditor to preview corner adjustments without saving the scene.
export async function previewComposite(body: {
  product_type: string;
  scene: SceneConfig;
  art_url?: string;
  size_w?: number;
  size_h?: number;
  frame_color?: string;
}): Promise<Blob> {
  const token = sessionStorage.getItem("admin_token") ?? "";
  const res = await fetch(`${API}/admin/preview-composite`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || res.statusText);
  }
  return res.blob();
}

// ── File Upload ──
export async function uploadAsset(file: File, category: "mockups" | "styles"): Promise<{ url: string }> {
  const token = sessionStorage.getItem("admin_token") ?? "";
  const form = new FormData();
  form.append("file", file);
  form.append("category", category);
  const res = await fetch(`${API}/admin/upload-asset`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) throw new Error("Upload failed");
  return res.json();
}
